import { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, CardBody, Button, Select, SelectItem } from '@nextui-org/react';
import { read, utils } from 'xlsx';
import {
  shipmentsExcelMapping,
  requiredFields,
  ShipmentRow,
} from '../../utils/shipments-excel-mapping';
import { toast } from 'react-toastify';
import { generateShipmentsTemplate } from '../../utils/shipments-template';
import { supabase } from 'common/src/supabase';
import { excelDateToJSDate } from '../../utils/functions';
import { importLocalShipments } from 'common/src/api/shipments';
import { hasShipmentUploadIdentifier } from 'common/src/utils/shipment-upload-validation';
import { ShipmentBox } from 'common/src/utils/types';
import useTrackingActivationStore, {
  type UploadedShipmentRow,
} from '../../store/useTrackingActivationStore';

// Date fields that need conversion from Excel numeric format
const dateFields = ['ship_etd', 'ship_eta'];

// Also fix the remaining linter errors by adding these properties to the type
type ExtendedShipmentRow = ShipmentRow & {
  season?: string | number;
  species_id?: string | number;
};

export const ShipmentsUpload = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedExporter, setSelectedExporter] = useState<string>('');
  const [fileData, setFileData] = useState<ExtendedShipmentRow[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [exporters, setExporters] = useState<{ id: string; name: string }[]>(
    []
  );
  const [loadingExporters, setLoadingExporters] = useState(true);
  const [isReadingFile, setIsReadingFile] = useState(false);
  const uploadInFlightRef = useRef(false);
  const fileReadIdRef = useRef(0);

  useEffect(() => () => { fileReadIdRef.current += 1; }, []);

  useEffect(() => {
    const fetchExporters = async () => {
      try {
        const { data, error } = await supabase
          .from('exporters')
          .select('id, name')
          .order('name');

        if (error) throw error;
        setExporters(data || []);
      } catch (error: unknown) {
        toast.error('Error al cargar exportadores');
      } finally {
        setLoadingExporters(false);
      }
    };

    fetchExporters();
  }, []);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file || uploadInFlightRef.current) return;
    const readId = ++fileReadIdRef.current;
    setFileName(file.name);
    setFileData([]);
    setIsReadingFile(true);

    const reader = new FileReader();
    reader.onload = async (e) => {
      if (readId !== fileReadIdRef.current) return;
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = utils.sheet_to_json(worksheet) as Record<
          string,
          string | number
        >[];

        // Validate and transform data
        const transformedData = jsonData.map((row, index) => {
          const transformedRow: Record<string, string | number> = {};
          transformedRow.season = row.Temporada ?? row.season ?? '';
          transformedRow.species_id = row['ID Especie'] ?? row.species_id ?? '';

          // Map Spanish column names to internal names
          Object.entries(shipmentsExcelMapping).forEach(
            ([spanishKey, internalKey]) => {
              if (row[spanishKey] !== undefined) {
                // Check if this is a date field and the value is a number (Excel date)
                if (
                  dateFields.includes(internalKey) &&
                  typeof row[spanishKey] === 'number'
                ) {
                  transformedRow[internalKey] = excelDateToJSDate(
                    row[spanishKey] as number
                  );
                } else {
                  transformedRow[internalKey] = row[spanishKey];
                }
              }
            }
          );

          // Validate required fields
          requiredFields.forEach((fieldDef) => {
            if (!transformedRow[fieldDef.field]) {
              throw new Error(
                `Fila ${index + 2}: Campo requerido "${fieldDef.label}" está vacío`
              );
            }
          });
          if (!hasShipmentUploadIdentifier(transformedRow)) {
            throw new Error(
              `Fila ${index + 2}: Debe incluir un número de contenedor o AWB`
            );
          }

          return transformedRow as unknown as ExtendedShipmentRow;
        });

        setFileData(transformedData);
      } catch (error: unknown) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error('Error al procesar el archivo');
        }
        setFileData([]);
      } finally {
        setIsReadingFile(false);
      }
    };
    reader.onerror = () => {
      if (readId !== fileReadIdRef.current) return;
      toast.error('Error al leer el archivo');
      setIsReadingFile(false);
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
    disabled: isUploading,
  });

  const handleUpload = async () => {
    if (uploadInFlightRef.current) return;
    if (!selectedExporter) {
      toast.error('Por favor seleccione un exportador');
      return;
    }

    if (fileData.length === 0) {
      toast.error('Por favor cargue un archivo válido');
      return;
    }

    uploadInFlightRef.current = true;
    setIsUploading(true);
    try {
      const season = String(fileData[0].season || '');
      const speciesId = Number(fileData[0].species_id);
      if (!season || !speciesId) {
        throw new Error('El archivo debe incluir Temporada e ID Especie.');
      }
      const uploadedRows = fileData;
      await importLocalShipments({
        exporterId: Number(selectedExporter),
        season,
        speciesId,
        mode: 'replace',
        rows: fileData.map((row) => ({
          ...row,
          exporter_id: Number(selectedExporter),
          season: String(row.season || ''),
          species_id: Number(row.species_id),
        })) as Partial<ShipmentBox>[],
      });
      toast.success('Archivo cargado exitosamente');
      // Tracking starts now instead of at the next scheduled run.
      void useTrackingActivationStore.getState().start({
        exporterId: Number(selectedExporter),
        season,
        speciesId,
        rows: uploadedRows as UploadedShipmentRow[],
      });
      setFileData([]);
      setFileName('');
      setSelectedExporter('');
    } catch (error: unknown) {
      if (error instanceof Error) {
        toast.error('Error al cargar el archivo: ' + error.message);
      } else {
        toast.error('Error al cargar el archivo');
      }
    } finally {
      uploadInFlightRef.current = false;
      setIsUploading(false);
    }
  };

  const downloadTemplate = () => {
    generateShipmentsTemplate();
  };

  return (
    <Card className='w-full'>
      <CardBody className='gap-4'>
        <div className='flex justify-between items-center'>
          <h2 className='text-xl font-bold'>Cargar Despachos</h2>
          <Button variant='flat' onPress={downloadTemplate}>
            Descargar Plantilla Tipo
          </Button>
        </div>

        <Select
          label='Exportador'
          placeholder='Seleccione un exportador'
          selectedKeys={selectedExporter ? [selectedExporter] : []}
          onSelectionChange={(keys) =>
            setSelectedExporter(String(Array.from(keys)[0] ?? ''))
          }
          isDisabled={loadingExporters || isUploading}
        >
          {exporters.map((exporter) => (
            <SelectItem key={exporter.id} value={exporter.id}>
              {exporter.name}
            </SelectItem>
          ))}
        </Select>
        <p className='text-sm text-default-500'>
          Incluye Temporada e ID Especie en el archivo. Se actualizan sus
          referencias conservando el ID del despacho; las referencias ausentes
          permanecen intactas.
        </p>

        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
            ${isDragActive ? 'border-primary bg-primary/10' : 'border-divider'}`}
        >
          <input {...getInputProps()} />
          {fileName ? (
            <p className='text-success'>Archivo seleccionado: {fileName}</p>
          ) : (
            <p>Arrastre un archivo Excel o haga click para seleccionar</p>
          )}
        </div>

        {fileData.length > 0 && (
          <div className='text-sm text-default-500'>
            {fileData.length} filas encontradas
          </div>
        )}

        <Button
          color='primary'
          isLoading={isUploading || isReadingFile}
          onPress={handleUpload}
          isDisabled={!selectedExporter || fileData.length === 0}
          className='w-full'
        >
          Cargar Archivo
        </Button>
      </CardBody>
    </Card>
  );
};
